/**
 * The RE2 helpers every GSC tool that builds or accepts a filter expression shares.
 *
 * Search Console reads `includingRegex` and `excludingRegex` as RE2, which is close to but not the
 * same language as JavaScript's RegExp. The browser can therefore only gate an expression — refuse
 * the ones RegExp cannot read at all — and never validate it: a pattern accepted here can still be
 * refused by Search Console, and that refusal comes back as the request's own error.
 */

/** RE2 metacharacters escaped, so a value is matched as the literal text it is. */
export function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The inline flag group RE2 understands at the front of a pattern and RegExp does not.
 *
 * Without lifting it off, every pattern written the way RE2 documents — `(?i)brand` — would be
 * rejected in the browser before Search Console ever saw it.
 */
export const INLINE_FLAGS = /^\(\?([imsU]+)\)/;

/**
 * A pattern written for RE2, read with RegExp — or a throw, with RegExp's reason, when it cannot be.
 *
 * The leading flag group is lifted off and its flags handed to RegExp instead, all but U, which
 * RegExp has no equivalent for. Used both as the syntax gate and wherever a report re-checks a
 * returned row against a rule in the browser.
 */
export function readPattern(pattern) {
    const flags = pattern.match(INLINE_FLAGS);
    if (!flags) return new RegExp(pattern);

    const jsFlags = [...new Set(flags[1].replace(/U/g, ''))].join('');
    return new RegExp(pattern.slice(flags[0].length), jsFlags);
}

/** Whether a pattern asks RE2 to ignore case through its leading flag group. */
export function ignoresCase(pattern) {
    return (pattern ?? '').match(INLINE_FLAGS)?.[1].includes('i') ?? false;
}
